import React, { useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledImage = styled('img')(({ theme }) => ({
  transition: 'opacity 0.3s ease-in-out',
  backgroundColor: '#f5f5f5'
}));

/**
 * OptimizedImage Component
 * 
 * Features:
 * - Lazy loading for performance
 * - Skeleton placeholder while loading
 * - Error handling with fallback UI
 * - Smooth fade-in transition
 * - Async image decoding
 * 
 * @param {string} src - Image source URL
 * @param {string} alt - Image alt text
 * @param {string} width - Image width (default: '100%')
 * @param {string} height - Image height (default: '100%')
 * @param {string} objectFit - CSS object-fit property (default: 'cover')
 * @param {string} borderRadius - Border radius (default: '0')
 * @param {object} sx - Additional MUI sx props
 * @param {string} fallbackText - Text to show on error (default: 'Image unavailable')
 * @param {boolean} eager - Disable lazy loading (default: false)
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  width = '100%',
  height = '100%',
  objectFit = 'cover',
  borderRadius = '0',
  sx = {},
  fallbackText = 'Image unavailable',
  eager = false,
  ...props 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleLoad = () => {
    setImageLoaded(true);
  };

  const handleError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  return (
    <Box 
      sx={{ 
        position: 'relative', 
        width, 
        height,
        overflow: 'hidden',
        ...sx 
      }}
    >
      {/* Skeleton loader - shown while image is loading */}
      {!imageLoaded && !imageError && (
        <Skeleton 
          variant="rectangular" 
          width={width} 
          height={height} 
          sx={{ 
            borderRadius,
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1
          }} 
          animation="wave"
        />
      )}
      
      {/* Actual Image */}
      <StyledImage
        src={src}
        alt={alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
        style={{ 
          opacity: imageLoaded ? 1 : 0,
          display: imageError ? 'none' : 'block',
          width,
          height,
          objectFit,
          borderRadius
        }}
        {...props}
      />
      
      {/* Error fallback */}
      {imageError && (
        <Box
          sx={{
            width,
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius,
            color: '#999',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif'
          }}
        >
          {fallbackText}
        </Box>
      )}
    </Box>
  );
};

export default OptimizedImage;
